/**
 * Gameplay Scene (Redesigned - Front-Only Forced Scrolling)
 * The camera relentlessly pushes forward. The Overfitter consumes from behind.
 * Stay on the regression line, avoid the deletion wall, survive.
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { Player } from '../entities/Player.js';
import { RegressionLine } from '../entities/RegressionLine.js';
import { GhostPath } from '../entities/GhostPath.js';
import { Overfitter } from '../entities/Overfitter.js';
import { DeletionWall } from '../entities/DeletionWall.js';
import { DataPointManager } from '../entities/DataPoint.js';
import { LeveragePointManager } from '../entities/LeveragePoint.js';
import { BossFight } from '../entities/BossFight.js';
import { ErrorBar } from '../ui/ErrorBar.js';
import { audioEngine } from '../systems/AudioEngine.js';
import { stateManager } from '../systems/StateManager.js';

export class GameplayScene extends Phaser.Scene {
    constructor() {
        super({ key: 'GameplayScene' });
    }

    init(data) {
        this.initialDistance = data.distance || 0;
        this.correctAnswers = data.correctAnswers || 0;
    }

    create() {
        // Dark horror background
        this.cameras.main.setBackgroundColor(CONSTANTS.COLORS.DARK_BG);
        this.cameras.main.fadeIn(500);

        // === FORCED SCROLLING SETUP ===
        this.scrollSpeed = CONSTANTS.SCROLL_SPEED_BASE;
        this.scrollSpeedMultiplier = 1;
        this.lastSpeedIncrease = 0;
        this.cameraX = 0;

        // Extend world bounds for scrolling
        this.physics.world.setBounds(0, 0, 100000, this.scale.height);

        // Game state
        this.errorLevel = 0;
        this.survivalTime = 0;
        this.distanceTraveled = this.initialDistance;
        this.gameOver = false;
        this.insufficientData = true; // Start with insufficient data (errors forgiven)

        // Boss fight state
        this.bossTriggered = false;
        this.bossFight = null;
        this.BOSS_TRIGGER_TIME = CONSTANTS.BOSS_TRIGGER_TIME;

        // Dread/horror state
        this.dreadLevel = 0;
        this.lastDreadTime = 0;
        this.screenTilt = 0;
        this.desaturation = 0;

        // Create scrolling background
        this.createBackground();

        // Create core entities
        this.createEntities();

        // Create UI (fixed to camera)
        this.createUI();

        // Setup shaders
        this.setupShaders();

        // Start audio
        this.setupAudio();
    }

    createBackground() {
        // Create multiple layers of grid for parallax scrolling effect
        this.gridGraphics = this.add.graphics();
        this.gridGraphics.setDepth(0);

        // Initial grid draw
        this.drawGrid(0);
    }

    drawGrid(cameraX) {
        this.gridGraphics.clear();

        const screenWidth = this.scale.width;
        const screenHeight = this.scale.height;

        // Calculate grid offset for seamless scrolling
        const gridSize = 50;
        const offsetX = cameraX % gridSize;

        // Draw vertical lines
        this.gridGraphics.lineStyle(1, CONSTANTS.COLORS.GRID_LINE, 0.3);
        for (let x = -offsetX; x <= screenWidth + gridSize; x += gridSize) {
            this.gridGraphics.moveTo(x + cameraX, 0);
            this.gridGraphics.lineTo(x + cameraX, screenHeight);
        }

        // Draw horizontal lines
        for (let y = 0; y <= screenHeight; y += gridSize) {
            this.gridGraphics.moveTo(cameraX, y);
            this.gridGraphics.lineTo(cameraX + screenWidth, y);
        }

        this.gridGraphics.strokePath();

        // X-axis (thicker, at vertical center)
        const centerY = screenHeight / 2;
        this.gridGraphics.lineStyle(2, CONSTANTS.COLORS.GRID_LINE, 0.5);
        this.gridGraphics.moveTo(cameraX, centerY);
        this.gridGraphics.lineTo(cameraX + screenWidth, centerY);
        this.gridGraphics.strokePath();
    }

    createEntities() {
        // Regression line (scrolls with camera)
        this.regressionLine = new RegressionLine(this);

        // Ghost path (uncertain flickering)
        this.ghostPath = new GhostPath(this, this.regressionLine);

        // Player (starts in safe zone)
        const startX = this.scale.width * CONSTANTS.PLAYER_X_POSITION;
        const startY = this.regressionLine.getYAtX(startX);
        this.player = new Player(this, startX, startY);

        // Connect player to regression line
        this.player.setRegressionLine(this.regressionLine);

        // Protection shield (visible during insufficient data phase)
        this.protectionShield = this.add.graphics();
        this.protectionShield.setDepth(49); // Just below player

        // Deletion Wall (the left edge of doom)
        this.deletionWall = new DeletionWall(this);

        // Overfitter (hidden during normal gameplay - revealed at boss fight!)
        this.overfitter = new Overfitter(this);
        this.overfitter.sprite.setAlpha(0); // Hidden until boss fight

        // Data point manager - points shape the line AND can be hit (overfitting!)
        this.dataPointManager = new DataPointManager(this, this.regressionLine);

        // RARE THREAT: Leverage point manager (gold high-influence points)
        this.leveragePointManager = new LeveragePointManager(this, this.regressionLine);
    }

    createUI() {
        const screenWidth = this.scale.width;
        const screenHeight = this.scale.height;

        // UI container that follows camera
        this.uiContainer = this.add.container(0, 0);
        this.uiContainer.setDepth(200);
        this.uiContainer.setScrollFactor(0);

        // Error bar (top center)
        this.errorBar = new ErrorBar(this, screenWidth / 2, 30);

        // Score display (top right, prominent) - score = time survived in seconds
        this.scoreContainer = this.add.container(screenWidth - 100, 30).setScrollFactor(0).setDepth(200);

        const scoreBg = this.add.graphics();
        scoreBg.fillStyle(0x000000, 0.5);
        scoreBg.fillRoundedRect(-80, -20, 160, 40, 8);
        this.scoreContainer.add(scoreBg);

        this.scoreLabel = this.add.text(-70, -10, 'SCORE', {
            fontSize: '10px',
            fontFamily: 'Courier New, monospace',
            color: '#888888'
        });
        this.scoreContainer.add(this.scoreLabel);

        this.scoreText = this.add.text(0, 2, '0', {
            fontSize: '22px',
            fontFamily: 'Courier New, monospace',
            color: '#FFFFFF',
            fontStyle: 'bold'
        }).setOrigin(0.5, 0.5);
        this.scoreContainer.add(this.scoreText);

        // Speed indicator (smaller, bottom left)
        this.speedText = this.add.text(50, screenHeight - 40, 'SPEED: 1.0x', {
            fontSize: '12px',
            fontFamily: 'Courier New, monospace',
            color: '#666666'
        }).setScrollFactor(0).setDepth(200);

        // === QUANTUM SUPERPOSITION UI ===
        this.quantumLabel = this.add.text(50, screenHeight - 80, '⚛ QUANTUM', {
            fontSize: '10px',
            fontFamily: 'Courier New, monospace',
            color: '#00FFFF'
        }).setScrollFactor(0).setDepth(200);

        // Quantum bar background
        this.quantumBarBg = this.add.rectangle(50, screenHeight - 60, 104, 12, 0x333333)
            .setOrigin(0, 0.5).setScrollFactor(0).setDepth(200);

        // Quantum bar fill
        this.quantumBar = this.add.rectangle(52, screenHeight - 60, 0, 8, 0x00FFFF)
            .setOrigin(0, 0.5).setScrollFactor(0).setDepth(201);

        // Quantum status text
        this.quantumStatus = this.add.text(160, screenHeight - 60, '', {
            fontSize: '10px',
            fontFamily: 'Courier New, monospace',
            color: '#00FFFF'
        }).setOrigin(0, 0.5).setScrollFactor(0).setDepth(200);

        // Boss progress indicator (bottom center)
        this.bossProgressText = this.add.text(screenWidth / 2, screenHeight - 40, 'BOSS: 0%', {
            fontSize: '14px',
            fontFamily: 'Courier New, monospace',
            color: '#FF6600'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(200);

        // Boss progress bar background
        this.bossProgressBg = this.add.rectangle(screenWidth / 2, screenHeight - 20, 154, 10, 0x333333)
            .setScrollFactor(0).setDepth(200);

        // Boss progress bar fill
        this.bossProgressBar = this.add.rectangle(screenWidth / 2 - 75, screenHeight - 20, 0, 8, 0xFF6600)
            .setOrigin(0, 0.5).setScrollFactor(0).setDepth(201);

        // Warning text (appears when close to wall) - center of screen
        this.warningText = this.add.text(screenWidth / 2, screenHeight / 2, 'DANGER', {
            fontSize: '48px',
            fontFamily: 'Courier New, monospace',
            color: '#FF0000',
            fontStyle: 'bold'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(201).setAlpha(0);

        // Insufficient data warning (shows when regression is unstable)
        this.dataWarning = this.add.text(screenWidth / 2, 70, '⚠ INSUFFICIENT DATA - LINE UNSTABLE', {
            fontSize: '14px',
            fontFamily: 'Courier New, monospace',
            color: '#FFAA00',
            fontStyle: 'bold'
        }).setOrigin(0.5).setScrollFactor(0).setDepth(201).setAlpha(1);
    }

    setupShaders() {
        this.corruptionPipeline = null;

        try {
            if (this.game.renderer.type === Phaser.WEBGL) {
                const pipelines = this.cameras.main.setPostPipeline('DataCorruption');
                if (pipelines && pipelines.length > 0 && pipelines[0].setIntensity) {
                    this.corruptionPipeline = pipelines[0];
                    this.corruptionPipeline.setIntensity(0);
                }
            }
        } catch (e) {
            console.log('Shader not available:', e);
            this.corruptionPipeline = null;
        }
    }

    setupAudio() {
        audioEngine.resume();
        audioEngine.startHeartbeat();
        audioEngine.startAmbientDrone();
    }

    update(time, delta) {
        if (this.gameOver) return;

        // Update survival metrics
        this.survivalTime += delta;
        this.distanceTraveled = this.cameraX;

        // === CHECK FOR BOSS TRIGGER ===
        if (!this.bossTriggered && this.survivalTime >= this.BOSS_TRIGGER_TIME) {
            this.triggerBossFight();
        }

        // === BOSS FIGHT MODE ===
        if (this.bossFight && this.bossFight.isActive()) {
            this.updateBossFight(delta);
            return;
        }

        // === NORMAL GAMEPLAY ===
        // === FORCED SCROLLING ===
        this.updateScrolling(delta);

        // Update camera position
        this.cameras.main.scrollX = this.cameraX;

        // Update background grid
        this.drawGrid(this.cameraX);

        // === UPDATE ENTITIES ===
        // Player needs camera position and scroll speed
        this.player.update(delta, this.cameraX, this.scrollSpeed);

        // Regression line scrolls with world
        this.regressionLine.update(delta, this.cameraX);

        // Ghost path
        this.ghostPath.update(delta, this.cameraX);

        // Deletion wall and Overfitter
        const wallProximity = this.deletionWall.update(
            delta,
            this.cameraX,
            this.player.sprite.x,
            this.player.sprite.y
        );

        this.overfitter.update(
            delta,
            this.cameraX,
            this.player.sprite.x,
            this.player.sprite.y
        );

        // Data points - shape the line AND can cause overfitting if touched!
        const dataPointResult = this.dataPointManager.update(delta, this.player, this.distanceTraveled, this.cameraX);
        if (dataPointResult.hit) {
            this.onThreatHit(dataPointResult);
        }

        // RARE THREAT: Leverage points - gold high-influence points
        const leverageResult = this.leveragePointManager.update(delta, this.player, this.cameraX);
        if (leverageResult.hit) {
            this.onThreatHit(leverageResult);
            // Extra visual chaos for leverage points
            this.cameras.main.shake(300, 0.02);
            this.cameras.main.flash(200, 255, 200, 0);
        }

        // === GAME MECHANICS ===
        this.checkLineCollision();
        this.checkGhostPathDread(delta);
        this.checkDeletion();
        this.updateErrorLevel(delta);
        this.updateHorrorEffects(delta, wallProximity);
        this.updateAudio();
        this.updateShaderIntensity(wallProximity);
        this.updateUI();

        // === GAME OVER CHECK ===
        const maxOverfit = this.player.currentScale >= this.player.maxScale;
        if (this.errorLevel >= CONSTANTS.ERROR_MAX || this.player.isDead || maxOverfit) {
            this.triggerGameOver();
        }

    }

    triggerBossFight() {
        this.bossTriggered = true;

        // Stop normal spawning
        this.dataPointManager.clear();
        this.leveragePointManager.clear();

        // Hide deletion wall during boss
        this.deletionWall.graphics.setAlpha(0);
        this.deletionWall.glowGraphics.setAlpha(0);

        // Hide ghost path for cleaner boss visuals
        this.ghostPath.graphics.setAlpha(0);

        // REVEAL the overfitter!
        this.overfitter.sprite.setAlpha(1);

        // Create boss fight
        this.bossFight = new BossFight(
            this,
            this.player,
            this.regressionLine,
            this.overfitter
        );

        // Start the boss fight
        this.bossFight.start();

        // Flash screen
        this.cameras.main.flash(500, 255, 0, 0);
    }

    updateBossFight(delta) {
        // Update camera position (stopped during boss)
        this.cameras.main.scrollX = this.cameraX;

        // Update background grid
        this.drawGrid(this.cameraX);

        // Update player
        this.player.update(delta, this.cameraX, 0);

        // Update regression line (boss controls it in phase 2)
        this.regressionLine.update(delta, this.cameraX);

        // Check line collision for damage/healing
        this.checkLineCollision();

        // Update error (so error bar changes during boss fight)
        this.updateErrorLevel(delta);

        // Update boss fight
        const bossResult = this.bossFight.update(delta, this.cameraX);

        // Update UI
        this.updateUI();

        // Check boss fight outcome
        if (bossResult.victory) {
            this.triggerVictory();
        }

        // Check for death conditions (same as normal gameplay)
        const maxOverfit = this.player.currentScale >= this.player.maxScale;
        if (bossResult.defeated || this.player.isDead || this.errorLevel >= CONSTANTS.ERROR_MAX || maxOverfit) {
            this.triggerGameOver();
        }
    }

    triggerVictory() {
        this.gameOver = true;

        // Celebration effects
        this.cameras.main.flash(1000, 255, 255, 255);

        // Save high score (using score = time survived in seconds)
        const finalScore = Math.floor(this.survivalTime / 1000);
        stateManager.updateHighScore(finalScore);

        // Transition to victory scene
        this.time.delayedCall(1500, () => {
            if (this.bossFight) this.bossFight.destroy();
            this.scene.start('VictoryScene', {
                score: finalScore,
                survivalTime: this.survivalTime
            });
        });
    }

    updateScrolling(delta) {
        // Increase speed over time
        this.lastSpeedIncrease += delta;

        if (this.lastSpeedIncrease >= CONSTANTS.SCROLL_SPEED_INTERVAL) {
            this.lastSpeedIncrease = 0;
            this.scrollSpeedMultiplier *= (1 + CONSTANTS.SCROLL_SPEED_INCREASE);

            // Cap maximum speed
            if (this.scrollSpeed * this.scrollSpeedMultiplier > CONSTANTS.SCROLL_SPEED_MAX) {
                this.scrollSpeedMultiplier = CONSTANTS.SCROLL_SPEED_MAX / CONSTANTS.SCROLL_SPEED_BASE;
            }

            // Visual feedback for speed increase
            this.cameras.main.flash(100, 50, 0, 0);
        }

        // Calculate current scroll speed
        const currentScrollSpeed = CONSTANTS.SCROLL_SPEED_BASE * this.scrollSpeedMultiplier;
        this.scrollSpeed = currentScrollSpeed;

        // Move camera forward
        this.cameraX += (currentScrollSpeed * delta) / 1000;
    }

    checkLineCollision() {
        // Get player distance from regression line
        const lineY = this.regressionLine.getYAtX(this.player.x);
        const playerY = this.player.y;
        const distance = Math.abs(playerY - lineY);

        // Tolerance zones - TIGHTER for harder gameplay
        const SAFE_ZONE = CONSTANTS.ZONE_SAFE;
        const DANGER_ZONE = CONSTANTS.ZONE_DANGER;
        const DEATH_ZONE = CONSTANTS.ZONE_DEATH;

        // During insufficient data phase, reduce errors (player sees feedback but won't die)
        const errorMultiplier = this.insufficientData ? 0.01 : 1;

        if (distance < SAFE_ZONE) {
            // Safe! On the line - recover error AND shrink (regularization!)
            this.player.isOnLine = true;
            this.errorLevel = Math.max(0, this.errorLevel - CONSTANTS.ERROR_RECOVERY_SAFE);

            // SHRINK while on line - reward for following the trend, not the points
            this.player.shrink(CONSTANTS.SHRINK_ON_LINE);
        } else if (distance < DANGER_ZONE) {
            // Warning zone - small error increase
            this.player.isOnLine = false;
            const severity = (distance - SAFE_ZONE) / (DANGER_ZONE - SAFE_ZONE);
            this.errorLevel += severity * CONSTANTS.ERROR_WARNING_RATE * errorMultiplier;
        } else if (distance < DEATH_ZONE) {
            // Danger zone - significant error increase
            this.player.isOnLine = false;
            const severity = (distance - DANGER_ZONE) / (DEATH_ZONE - DANGER_ZONE);
            this.errorLevel += (CONSTANTS.ERROR_DANGER_BASE + severity * CONSTANTS.ERROR_DANGER_MAX) * errorMultiplier;

            // Screen shake warning (only if accountable)
            if (!this.insufficientData && Math.random() < 0.1) {
                this.cameras.main.shake(50, 0.005);
            }
        } else {
            // Death zone - rapid error increase
            this.player.isOnLine = false;
            this.errorLevel += CONSTANTS.ERROR_DEATH_RATE * errorMultiplier;

            // Heavy visual feedback (only if accountable)
            if (!this.insufficientData) {
                this.cameras.main.shake(100, 0.01);
            }
        }
    }

    checkGhostPathDread(delta) {
        this.player.checkGhostPath(this.ghostPath);

        // THREAT #3: NOISE - Being on ghost path causes slow continuous growth
        if (this.player.isOnGhostPath) {
            // Slow growth while on ghost path (fitting noise instead of signal)
            this.player.grow(CONSTANTS.GROWTH_GHOST_PATH * delta / 1000);
        }

        const shouldTriggerDread = this.player.updateGhostPathTime(delta);
        const timeSinceLastDread = this.time.now - this.lastDreadTime;

        if (shouldTriggerDread && timeSinceLastDread > CONSTANTS.GHOST_DREAD_COOLDOWN) {
            this.triggerSubtleDread();
            this.lastDreadTime = this.time.now;
            this.player.ghostPathTime = 0;
        }
    }

    checkDeletion() {
        // Check if player touched the deletion wall
        if (this.player.checkDeletion(this.cameraX)) {
            // Instant death - consumed by the Overfitter
            this.errorLevel = CONSTANTS.ERROR_MAX;
        }
    }

    triggerSubtleDread() {
        audioEngine.playSubtleDread();

        if (this.corruptionPipeline && this.corruptionPipeline.setIntensity) {
            const originalIntensity = this.corruptionPipeline.getIntensity ?
                this.corruptionPipeline.getIntensity() : 0;
            this.corruptionPipeline.setIntensity(0.8);

            this.time.delayedCall(200, () => {
                if (this.corruptionPipeline && this.corruptionPipeline.setIntensity) {
                    this.corruptionPipeline.setIntensity(originalIntensity);
                }
            });
        }

        this.cameras.main.flash(150, 100, 0, 0, true);
        this.cameras.main.shake(200, 0.01);
        this.errorLevel += CONSTANTS.ERROR_GHOST_PATH_DAMAGE;
    }

    updateErrorLevel(delta) {
        // Slow decay when on line
        if (this.player.isOnLine) {
            this.errorLevel = Math.max(0, this.errorLevel - CONSTANTS.ERROR_RECOVERY_ONLINE);
        }

        // Clamp error
        this.errorLevel = Phaser.Math.Clamp(this.errorLevel, 0, CONSTANTS.ERROR_MAX);

        // Update error bar
        this.errorBar.setError(this.errorLevel);

        // Ghost path flicker increases with error
        const flickerRate = (this.errorLevel / CONSTANTS.ERROR_MAX) * 0.5;
        this.ghostPath.setFlickerRate(flickerRate);
    }

    updateHorrorEffects(delta, wallProximity) {
        const panicLevel = this.player.getPanicLevel();
        const combinedThreat = Math.max(wallProximity, panicLevel);

        // Screen tilt when close to wall
        this.screenTilt = Phaser.Math.Linear(
            this.screenTilt,
            combinedThreat * 0.03 * Math.sin(this.time.now * 0.002),
            0.1
        );
        this.cameras.main.setRotation(this.screenTilt);

        // Warning text
        if (combinedThreat > 0.6) {
            const pulse = Math.sin(this.time.now * 0.01) * 0.5 + 0.5;
            this.warningText.setAlpha(combinedThreat * pulse);

            // Shake more as threat increases
            if (combinedThreat > 0.8 && Math.random() < 0.1) {
                this.cameras.main.shake(100, 0.005 * combinedThreat);
            }
        } else {
            this.warningText.setAlpha(0);
        }

        // Increase error when too close to wall
        if (combinedThreat > 0.7) {
            this.errorLevel += combinedThreat * 0.05;
        }
    }

    updateAudio() {
        // Combine error and panic for tension
        const panicLevel = this.player.getPanicLevel();
        const combinedTension = Math.max(this.errorLevel, panicLevel * 100);
        audioEngine.setTension(combinedTension);
        audioEngine.update(16);
    }

    updateShaderIntensity(wallProximity) {
        if (!this.corruptionPipeline || !this.corruptionPipeline.setIntensity) return;

        const errorIntensity = this.errorLevel / CONSTANTS.ERROR_MAX;
        const panicIntensity = this.player.getPanicLevel();
        const overfitIntensity = this.player.getOverfitLevel();

        // Combined intensity - overfitting also increases visual corruption
        const intensity = Math.max(
            errorIntensity * 0.6,
            wallProximity * 0.8,
            panicIntensity * 0.7,
            overfitIntensity * 0.5
        );

        this.corruptionPipeline.setIntensity(intensity);
    }

    updateUI() {
        this.errorBar.update();

        // Update score (time survived in seconds)
        const score = Math.floor(this.survivalTime / 1000);
        this.scoreText.setText(`${score}`);

        // Update speed indicator
        const speedDisplay = this.scrollSpeedMultiplier.toFixed(1);
        this.speedText.setText(`SPEED: ${speedDisplay}x`);

        // Color speed text based on danger
        if (this.scrollSpeedMultiplier > 2) {
            this.speedText.setColor('#FF4444');
        } else if (this.scrollSpeedMultiplier > 1.5) {
            this.speedText.setColor('#FFAA44');
        } else {
            this.speedText.setColor('#888888');
        }

        // === UPDATE QUANTUM UI ===
        const quantumPercent = this.player.getQuantumChargePercent();

        if (this.player.isQuantumOnCooldown()) {
            // Cooldown state - gray bar draining
            const cooldownPercent = 1 + quantumPercent; // quantumPercent is negative during cooldown
            this.quantumBar.setSize(100 * cooldownPercent, 8);
            this.quantumBar.setFillStyle(0x666666);
            this.quantumLabel.setColor('#666666');
            this.quantumStatus.setText('COOLDOWN');
            this.quantumStatus.setColor('#666666');
        } else if (this.player.isQuantumReady()) {
            // Ready state - full cyan bar, pulsing
            this.quantumBar.setSize(100, 8);
            this.quantumBar.setFillStyle(0x00FFFF);
            this.quantumLabel.setColor('#00FFFF');
            this.quantumStatus.setText('READY!');
            this.quantumStatus.setColor('#00FF00');
        } else {
            // Charging state
            this.quantumBar.setSize(100 * quantumPercent, 8);
            this.quantumBar.setFillStyle(0x00AAAA);
            this.quantumLabel.setColor('#00AAAA');
            const percent = Math.floor(quantumPercent * 100);
            this.quantumStatus.setText(`${percent}%`);
            this.quantumStatus.setColor('#00AAAA');
        }

        // Update boss progress (only during normal gameplay)
        if (!this.bossTriggered) {
            const bossProgress = Math.min(this.survivalTime / this.BOSS_TRIGGER_TIME, 1);
            const bossPercent = Math.floor(bossProgress * 100);
            this.bossProgressText.setText(`BOSS: ${bossPercent}%`);
            this.bossProgressBar.setSize(150 * bossProgress, 8);

            // Color changes as boss approaches
            if (bossProgress > 0.8) {
                this.bossProgressText.setColor('#FF0000');
                this.bossProgressBar.setFillStyle(0xFF0000);
            } else if (bossProgress > 0.5) {
                this.bossProgressText.setColor('#FFAA00');
                this.bossProgressBar.setFillStyle(0xFFAA00);
            }
        } else {
            // Hide progress during boss fight
            this.bossProgressText.setAlpha(0);
            this.bossProgressBg.setAlpha(0);
            this.bossProgressBar.setAlpha(0);
        }

        // Update insufficient data warning (only during normal gameplay, not boss fight)
        if (this.bossTriggered) {
            this.insufficientData = false;
            this.protectionShield.clear();
            this.dataWarning.setAlpha(0);
        }

        const visibleDataPoints = this.dataPointManager.getVisiblePointCount(this.cameraX);
        const MIN_STABLE_POINTS = 5;
        if (!this.bossTriggered) {
            this.insufficientData = visibleDataPoints < MIN_STABLE_POINTS;
        }

        if (this.insufficientData) {
            // Show warning with flicker effect for unstable feel
            const flicker = Math.sin(this.time.now * 0.01) * 0.2 + 0.8;
            this.dataWarning.setAlpha(flicker);
            this.dataWarning.setText(`⚠ INSUFFICIENT DATA (${visibleDataPoints}/${MIN_STABLE_POINTS})\n"Judging you... but only 1%"`);
            this.dataWarning.setColor('#00FF00'); // Green = mostly safe

            // Draw pulsing protection shield around player
            this.protectionShield.clear();
            const pulse = Math.sin(this.time.now * 0.008) * 0.3 + 0.5;
            const shieldRadius = 35 + Math.sin(this.time.now * 0.006) * 5;

            // Outer glow
            this.protectionShield.lineStyle(8, 0x00FF00, pulse * 0.3);
            this.protectionShield.strokeCircle(this.player.x, this.player.y, shieldRadius + 8);

            // Inner shield
            this.protectionShield.lineStyle(3, 0x00FF00, pulse * 0.7);
            this.protectionShield.strokeCircle(this.player.x, this.player.y, shieldRadius);

            // Shield fill (very subtle)
            this.protectionShield.fillStyle(0x00FF00, pulse * 0.1);
            this.protectionShield.fillCircle(this.player.x, this.player.y, shieldRadius);
        } else if (this.dataWarning.alpha > 0) {
            // Clear shield when transitioning out
            this.protectionShield.clear();
            // Transition message before fading
            if (this.dataWarning.alpha > 0.5) {
                this.dataWarning.setText('✓ DATA SUFFICIENT - You are now accountable!');
                this.dataWarning.setColor('#FFAA00'); // Orange = warning
            }
            // Fade out warning smoothly
            this.dataWarning.setAlpha(Math.max(0, this.dataWarning.alpha - 0.01));
        }
    }

    // Called when player hits any statistical threat (data point, leverage point)
    onThreatHit(result) {
        // Check if quantum superposition can absorb the hit
        if (this.player.tryQuantumDodge()) {
            // Quantum absorbed the hit! No damage taken.
            return;
        }

        // Growth is the punishment - OVERFITTING!
        if (!this.player.isInvincible) {
            this.player.grow(result.growth);

            // Brief invincibility to prevent rapid hits
            this.player.isInvincible = true;
            this.player.invincibilityTimer = 300; // Shorter for data points

            // Visual feedback based on threat type
            if (result.type === 'leverage') {
                this.cameras.main.shake(300, 0.025);
                this.cameras.main.flash(200, 255, 215, 0); // Gold flash
            } else if (result.type === 'datapoint') {
                // Subtle feedback for regular data points
                this.cameras.main.shake(80, 0.008);
                this.cameras.main.flash(50, 100, 200, 255); // Cyan flash (data color)
                // Error penalty for overfitting
                this.errorLevel += CONSTANTS.ERROR_DATAPOINT_HIT;
            }
        }
    }

    // Called when player answers wrong (increases speed)
    onMathError() {
        this.scrollSpeedMultiplier *= (1 + CONSTANTS.SCROLL_SPEED_INCREASE);
        this.errorLevel += CONSTANTS.ERROR_OUTLIER_DAMAGE;

        // Visual/audio feedback
        this.cameras.main.shake(300, 0.02);
        this.cameras.main.flash(200, 100, 0, 0);
        audioEngine.playDamage();
    }

    // Called when player answers correctly
    onCorrectAnswer() {
        audioEngine.playCorrect();

        // Small error reduction
        this.errorLevel = Math.max(0, this.errorLevel - 5);
    }

    triggerGameOver() {
        this.gameOver = true;

        // Stop audio
        audioEngine.stopHeartbeat();
        audioEngine.stopAmbientDrone();
        audioEngine.playGameOver();

        // Record stats (score = time survived in seconds)
        const finalScore = Math.floor(this.survivalTime / 1000);
        stateManager.recordDeath();
        stateManager.recordSurvivalTime(this.survivalTime);
        const isHighScore = stateManager.updateHighScore(finalScore);

        // Death effects
        this.cameras.main.flash(500, 100, 0, 0);
        this.cameras.main.shake(500, 0.03);

        this.time.delayedCall(1500, () => {
            this.scene.start('GameOverScene', {
                score: finalScore,
                survivalTime: this.survivalTime,
                isHighScore: isHighScore
            });
        });
    }

    shutdown() {
        audioEngine.stopHeartbeat();
        audioEngine.stopAmbientDrone();
    }
}
